<script>
  /**
   * Shows whether automatically saved changes are stored.
   * While saving, icon and text are shown. Once saved, only the icon remains
   * and the saved text is available as tooltip and to screen readers.
   * "Saving" stays visible for at least `minSavingDuration`,
   * so users notice it even when the request is very fast.
   */

  /** Set to `true` while a save request is running */
  export let saving = false;

  /** Minimum time in milliseconds the saving state stays visible */
  export let minSavingDuration = 1000;

  /** Text shown while saving */
  export let savingText = "Saving";

  /** Tooltip and screen reader text once everything is saved */
  export let savedText = "All changes saved";

  import { onDestroy } from "svelte";
  import { fly } from "svelte/transition";

  // Material Symbols Outlined, weight 100: "backup" while saving, "cloud_done" once saved
  const savingIcon =
    "M260-212q-70 0-119-48.77Q92-309.55 92-380q0-69 49.5-118.5T254-548q10-86 74.5-143T480-748q95.27 0 161.64 66.36Q708-615.27 708-520v52h32q54 0 91 37t37 91q0 54-36 91t-90 37H526q-26 0-43-17t-17-43v-240l-90 90-20-19 124-124 124 124-20 19-90-90v240q0 12 10 22t22 10h214q42 0 71-29t29-71q0-42-29-71t-71-29h-60v-80q0-83-58.5-141.5T480-720q-83 0-141.5 58.5T280-520h-22q-55 0-96.5 41T120-380q0 58 41 99t99 41h100v28H260Zm220-254Z";
  const savedIcon =
    "m414-316 190-190-20-20-170 170-86-86-20 20 106 106ZM260-212q-70 0-119-49T92-380q0-66 47-117t115-51q10-86 74.5-143T480-748q95 0 161.5 66.5T708-520v52h32q54 0 91 37t37 91q0 54-37 91t-91 37H260Zm0-28h480q42 0 71-29t29-71q0-42-29-71t-71-29h-60v-80q0-83-58.5-141.5T480-720q-83 0-141.5 58.5T280-520h-20q-58 0-99 41t-41 99q0 58 41 99t99 41Zm220-240Z";

  let showSaving = saving;
  let savingStartedAt = saving ? Date.now() : 0;
  let hideTimer;

  $: update(saving);

  function update(isSaving) {
    clearTimeout(hideTimer);
    if (isSaving) {
      // Each new save restarts the minimum duration, also while the previous one is still shown
      savingStartedAt = Date.now();
      showSaving = true;
      return;
    }
    if (!showSaving) return;
    const remaining = minSavingDuration - (Date.now() - savingStartedAt);
    if (remaining <= 0) showSaving = false;
    else hideTimer = setTimeout(() => (showSaving = false), remaining);
  }

  onDestroy(() => clearTimeout(hideTimer));
</script>

<span
  class:bx--save-status="{true}"
  class:bx--save-status--saving="{showSaving}"
  data-tooltip="{showSaving ? undefined : savedText}"
  {...$$restProps}
>
  <!-- Old and new text share one grid cell, so they can cross over during the transition -->
  <span class:bx--save-status__track="{true}">
  {#key showSaving}
    <span
      class:bx--save-status__state="{true}"
      aria-hidden="true"
      in:fly="{{ y: 8, duration: 200, delay: 100 }}"
      out:fly="{{ y: -8, duration: 150 }}"
    >
      <svg class:bx--save-status__icon="{true}" viewBox="0 -960 960 960"
        ><path d="{showSaving ? savingIcon : savedIcon}"></path></svg
      >
      <!-- Saved shows the icon only, the text appears while saving so the change gets noticed -->
      {#if showSaving}{savingText}{/if}
    </span>
  {/key}
  </span>
  <span class:bx--save-status__label="{true}" role="status" aria-live="polite"
    >{showSaving ? savingText : savedText}</span
  >
</span>

<style>
  /* Height of a header button, type of the header breadcrumb */
  .bx--save-status {
    position: relative;
    display: inline-flex;
    color: var(--pax-grey, #929292);
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.25;
    white-space: nowrap;
  }

  /* Clips the fly transition, the tooltip sits outside of it */
  .bx--save-status__track {
    display: inline-grid;
    box-sizing: border-box;
    height: 2.5rem;
    align-items: center;
    overflow: hidden;
  }

  /* Same look as the tooltips of the side navigation */
  .bx--save-status[data-tooltip]:hover::after {
    content: attr(data-tooltip);
    position: absolute;
    top: calc(100% + 0.25rem);
    right: 0;
    z-index: 9000;
    padding: 0.4rem 0.55rem;
    border-radius: 0.25rem;
    background: var(--pax-black, #1c1c20);
    color: var(--pax-white, #ffffff);
    font-size: 0.75rem;
    line-height: 1rem;
    pointer-events: none;
  }

  /* Right aligned: the status sits next to the header buttons, so its right edge stays put when the text width changes */
  .bx--save-status__state {
    display: inline-flex;
    grid-area: 1 / 1;
    justify-self: end;
    align-items: center;
    justify-content: flex-end;
    gap: 0.375rem;
  }

  .bx--save-status__icon {
    flex: 0 0 auto;
    width: 1.5rem;
    height: 1.5rem;
    fill: currentColor;
  }

  .bx--save-status__label {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }
</style>
